import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import hero from "@/assets/cooling/outdoor-hvac-heating-hero.jpg";
import heroPicture from "@/assets/cooling/outdoor-hvac-heating-hero.jpg?w=640;1024;1600&format=avif;webp;jpeg&as=picture";

const FAQS = [
  {
    q: "Can a mini-split cool an outdoor patio?",
    a: "A mini-split can effectively condition an enclosed or semi-enclosed patio. It cannot maintain a conventional room temperature across a completely open area.",
  },
  {
    q: "Can you cool an outdoor kitchen?",
    a: "Yes. Depending on its construction, options may include a mini-split, concealed ductwork or portable refrigerated spot cooling.",
  },
  {
    q: "Do mini-splits provide heat?",
    a: "Most mini-split heat pumps provide both refrigerated cooling and efficient heating.",
  },
  {
    q: "What is the best heat for an open patio?",
    a: "Infrared radiant heaters are usually more effective than equipment that attempts to heat the surrounding outdoor air.",
  },
  {
    q: "Are portable outdoor air conditioners real AC?",
    a: "Refrigerant-based models produce genuine cold, dehumidified air. They provide targeted cooling rather than whole-patio temperature control.",
  },
  {
    q: "Do you offer permanent and portable equipment?",
    a: "Yes. The appropriate solution depends on the structure, exposure, utility connections and whether you need targeted or whole-space comfort.",
  },
  {
    q: "Do you install outdoor heating and cooling for hotels, restaurants, and event venues?",
    a: "Yes. We design and install climate systems for hospitality and commercial outdoor spaces — including hotel pool decks, restaurant patios, brewery and distillery tasting areas, spa courtyards, wedding venues, event lawns, glamping tent resorts and outdoor retail showrooms.",
  },
];

const SYSTEM_PICKER: { space: string; start: string }[] = [
  { space: "Fully enclosed room", start: "Mini-split heat pump" },
  { space: "Porch with retractable enclosure", start: "Mini-split or concealed ducted system" },
  { space: "Open covered porch", start: "Portable refrigerated spot cooling" },
  { space: "Outdoor-kitchen work area", start: "Directional portable AC" },
  { space: "Enclosed glamping tent or cabin", start: "Dedicated mini-split" },
  { space: "Open patio needing permanent heat", start: "Mounted infrared heater" },
  { space: "Flexible seating arrangement", start: "Portable heater" },
  { space: "Year-round outdoor room", start: "Mini-split plus outdoor radiant heat" },
  { space: "Hotel pool deck or spa courtyard", start: "Mini-split for cabanas + infrared radiant heat" },
  { space: "Restaurant patio with flexible seating", start: "Portable heaters + targeted spot cooling" },
  { space: "Glamping tent village or safari venue", start: "Dedicated mini-splits or portable heat-pump units" },
  { space: "Outdoor event lawn or wedding venue", start: "Portable heaters + spot coolers for seasonal use" },
  { space: "Outdoor retail showroom or car lot", start: "Ceiling infrared + portable AC" },
];

const TITLE = "Outdoor Heating & Cooling — Residential & Commercial Mini-Splits, Patio Heaters & Portable AC | Texas Made Shade";
const DESCRIPTION =
  "Heating and refrigerated cooling for outdoor living and commercial spaces — mini-split heat pumps, outdoor-rated portable air conditioners and radiant patio heaters for porches, outdoor kitchens, glamping tents, hotels, spas, restaurants, event venues and covered hospitality areas across San Antonio, Austin, New Braunfels & Seguin.";

export const Route = createFileRoute("/outdoor-hvac")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESCRIPTION,
      image: hero,
      socialTitle: "Outdoor Heating & Cooling — Refrigerated Comfort for Outdoor Living",
      socialDescription:
        "Mini-split heat pumps, portable refrigerated air conditioners and radiant patio heaters, planned around the construction and exposure of your outdoor space.",
      keywords:
        "San Antonio outdoor cooling company, Austin outdoor heating company, outdoor cooling company San Antonio TX, outdoor heating and cooling company Texas, outdoor AC company San Antonio, patio cooling company Austin, outdoor heater company Texas, mini split installers San Antonio, mini split patio Austin, mini split heat pump porch, outdoor kitchen air conditioning, portable outdoor air conditioner Texas, spot cooler patio, infrared patio heater San Antonio, outdoor patio heating, glamping tent mini split, workshop mini split Seguin, commercial outdoor heating and cooling Texas, hotel patio heating, restaurant outdoor AC, spa courtyard climate control, event venue portable heater, glamping tent HVAC",
    }),
    scripts: serviceHeadScripts({
      path: "/outdoor-hvac",
      serviceName: "Outdoor Heating & Cooling",
      serviceType:
        "Mini-split heat pump, refrigerated outdoor cooling, portable air conditioning and outdoor radiant heating design and installation",
      breadcrumbName: "Outdoor Heating & Cooling",
      description:
        "Heating and refrigerated cooling for outdoor living and commercial spaces — mini-split heat pumps, concealed ducted systems, outdoor-rated portable air conditioners and radiant patio heaters for porches, outdoor kitchens, workshops, glamping structures, hotels, spas, restaurants, event venues and covered hospitality areas across San Antonio, Austin, New Braunfels and Seguin.",
      faqs: FAQS,
    }),
  }),
  component: OutdoorHvacPage,
});

function OutdoorHvacPage() {
  return (
    <ServicePage
      currentPath="/outdoor-hvac"
      serviceName="Outdoor Heating & Cooling"
      breadcrumbs={[
        { label: "Outdoor Living", to: "/hill-country-series" },
        { label: "Outdoor Heating & Cooling" },
      ]}
      keywordOutline={[
        { id: "overview", label: "Outdoor Heating & Cooling" },
        { id: "why-us", label: "Why Us — Outdoor Comfort" },
        { id: "details", label: "Mini-Splits, Heating & Portable Equipment" },
        { id: "7-commercial-outdoor-heating-and-cooling", label: "Commercial Outdoor HVAC" },
        { id: "system-picker", label: "Which System Is Right?" },
        { id: "applications", label: "Where We Install" },
        { id: "faqs", label: "Outdoor Heating & Cooling FAQs" },
      ]}
      whyUsPlacement="afterHero"
      eyebrow="★ HILL COUNTRY SERIES™ · OUTDOOR LIVING & PREMIUM SHADE ★"
      title="Heating and Refrigerated Cooling for Outdoor Living"
      intro="Make porches, outdoor kitchens, workshops, glamping structures and covered entertainment areas more comfortable throughout the year — from residential backyards to commercial hospitality and outdoor-event spaces such as hotels, spas, restaurants, glamping tent venues and event lawns. Texas Made Shade & Covers offers permanent and portable heating and cooling solutions selected around the construction, exposure and intended use of each space."
      heroImage={heroPicture}
      heroImageClassName="opacity-60"
      heroOverlayClassName="bg-gradient-to-r from-navy-deep/85 via-navy-deep/55 to-navy-deep/10"
      preContent={
        <section className="bg-navy-deep py-14 text-cream md:py-16">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-[minmax(0,260px)_1fr] lg:px-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <SeriesBadge slug="hill-country-series" size="lg" priority />
              <div>
                <div className="font-display text-xs uppercase tracking-[0.3em] text-cream/70">
                  Primary Badge
                </div>
                <div className="mt-1 font-display text-lg font-bold">Hill Country Series™</div>
                <div className="text-sm text-cream/80">Outdoor Living &amp; Premium Shade</div>
              </div>
            </div>
            <div>
              <h2 className="font-display text-3xl font-bold leading-tight md:text-4xl">
                Three ways to condition an outdoor space: mini-splits, outdoor heating, portable
                equipment
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-cream/85">
                From energy-efficient mini-split heat pumps to outdoor-rated portable air
                conditioners and radiant patio heaters, we help determine which equipment makes
                sense for your application.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/estimate-quotes"
                  className="border-2 border-cream bg-red px-5 py-3 font-display text-sm uppercase tracking-wider text-white hover:bg-red-deep"
                >
                  Request an Estimate →
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-cream px-5 py-3 font-display text-sm uppercase tracking-wider hover:bg-cream hover:text-navy-deep"
                >
                  Talk With Our Climate Team →
                </Link>
              </div>
            </div>
          </div>
        </section>
      }
      features={[
        "Mini-split heat pumps — genuine refrigerated cooling and efficient heat",
        "Single-zone, multi-zone, cassette and concealed ducted configurations",
        "Refrigerated cooling planned for covered outdoor areas",
        "Outdoor-rated portable and spot-cooling equipment",
        "Permanent electric, natural-gas and propane radiant heaters",
        "Portable heaters for flexible seating and event use",
        "Enclosure — screens and clear curtains — planned with the equipment",
      ]}
      body={[
        {
          heading: "1. Mini-Split Heating and Cooling",
          text: "Mini-split heat pumps provide genuine refrigerated air conditioning and efficient heating without traditional ductwork. A compact outdoor condenser connects to an indoor air handler, allowing individual spaces to be conditioned independently. Mini-splits work best in spaces that can retain conditioned air. An entirely open patio cannot be maintained at the same temperature as an enclosed room. Retractable screens, clear weather curtains and other enclosure options may improve performance and expand how the space can be used.",
          bullets: [
            "Good for enclosed and semi-enclosed porches",
            "Outdoor kitchens with screens or curtains",
            "Workshops, garages, pool houses and guest spaces",
            "Glamping tents and cabins",
            "Showrooms, small commercial areas and additions without ductwork",
            "Configurations: wall-mounted, ceiling-cassette or concealed ducted",
            "Single-zone or multi-zone; cooling-only or heat-pump heating and cooling",
            "Wi-Fi and smart controls",
          ],
          cta: { label: "Ask about a mini-split", href: "/estimate-quotes#general" },
        },
        {
          heading: "2. Refrigerated Cooling for Covered Outdoor Areas",
          text: "Covered outdoor spaces require a different approach than conventional indoor rooms. Sun exposure, humidity, wind, open walls and heat from cooking equipment all affect system performance. We evaluate the structure and recommend either a permanent mini-split, concealed ducted system or targeted portable air conditioner based on how open the area is and where cooling is needed.",
          bullets: [
            "Number of open sides",
            "Roof and ceiling construction",
            "Afternoon sun exposure",
            "Grill and cooking-equipment heat",
            "Exhaust and ventilation requirements",
            "Electrical capacity",
            "Equipment weather protection and condensation drainage",
            "Available screen or curtain enclosure",
          ],
          cta: { label: "Explore motorized screens", href: "/exterior-vertical-shades" },
        },
        {
          heading: "3. Portable Refrigerated Air Conditioners",
          text: "Portable outdoor-rated air conditioners use a compressor and refrigerant to produce genuine cold air. They are designed to direct conditioned air toward a specific seating, dining or work zone rather than cool an entire open patio. Portable outdoor AC provides targeted comfort. Coverage depends on BTU capacity, airflow, humidity, shade and the openness of the area.",
          bullets: [
            "Outdoor-kitchen workstations and covered seating areas",
            "Event and hospitality spaces; temporary installations",
            "Workshops, garages and glamping tents",
            "Areas where permanent equipment is impractical",
            "Compact 120-volt units and commercial spot coolers",
            "Directional cold-air ducts",
            "Portable heat-pump units for supplemental cooling",
          ],
        },
        {
          heading: "4. Outdoor Patio Heating",
          text: "Radiant outdoor heaters warm people and surrounding surfaces directly instead of attempting to heat all the outdoor air. Permanent systems can be integrated into patio covers, porches, pergolas and outdoor kitchens. Heater selection and placement depend on mounting height, required clearances, available electricity or gas, ventilation and surrounding combustible materials.",
          bullets: [
            "Electric infrared, natural-gas and propane heaters",
            "Wall-mounted, ceiling-mounted and recessed architectural heaters",
            "Directional radiant heaters",
            "Smart and remote-controlled systems",
            "Residential porches, outdoor kitchens and poolside seating",
            "Restaurant patios, hospitality and resort spaces",
            "Pergolas and pavilions",
          ],
          cta: { label: "Ask about patio heating", href: "/estimate-quotes#general" },
        },
        {
          heading: "5. Portable Outdoor Heaters",
          text: "Portable heaters provide flexible warmth without requiring a permanently mounted system. They can be moved between seating, dining and work areas as conditions change. Portable heaters must be used according to the manufacturer's clearance, ventilation and location requirements — not every portable heater is approved for covered or partially enclosed spaces.",
          bullets: [
            "Freestanding propane patio heaters",
            "Portable electric infrared heaters",
            "Tabletop heaters",
            "Commercial directional heaters",
            "Temporary construction and event heaters",
          ],
        },
        {
          heading: "6. Designed Around Your Outdoor Space",
          text: "Texas Made Shade can coordinate climate equipment with patio covers, canopies, retractable awnings, motorized screens, clear weather curtains, lighting and custom fabrication. This allows the structure and climate system to be planned together instead of added as unrelated components. Heating and air-conditioning work is performed under the supervision of a Texas-licensed Air Conditioning and Refrigeration professional. Equipment sizing, electrical requirements, refrigerant work and final installation are evaluated for each project.",
          bullets: [
            "Structure and equipment planned in one design",
            "Screens, curtains and covers sized to hold conditioned air",
            "Controls grouped with lighting and screens",
            "Licensed air conditioning and refrigeration supervision",
          ],
          cta: { label: "See smart automation", href: "/lone-star-elite" },
        },
        {
          heading: "7. Commercial Outdoor Heating & Cooling",
          text: "Texas Made Shade designs, sources and installs heating and cooling systems for commercial and hospitality outdoor spaces across Central and South Texas. From glamping tent resorts and safari tent venues to hotel pool decks, spa courtyards, restaurant patios, bars, breweries, wedding lawns and outdoor retail showrooms, we plan equipment around how the space is used, how open it is, and how guests move through it. Solutions range from permanent mini-split heat pumps for enclosed or semi-enclosed structures to portable spot coolers, infrared radiant heaters and flexible seasonal equipment for event seating. We also coordinate climate systems with motorized screens, clear weather curtains, pergolas, pavilions and custom fabric enclosures so the structure and the equipment work as one package.",
          bullets: [
            "Glamping tent resorts, safari tent venues and luxury outdoor lodging",
            "Hotel pool decks, courtyards, terraces and spa outdoor lounges",
            "Restaurant, bar, brewery, distillery and tasting patios",
            "Wedding venues, event lawns and outdoor reception spaces",
            "Outdoor retail showrooms, car lots and hospitality entrances",
            "Office courtyards, co-working lounges and outdoor meeting areas",
            "Permanent and seasonal solutions — mounted, portable or a mix",
            "Coordinated with screens, curtains, awnings, pergolas and custom enclosures",
          ],
          cta: { label: "Plan a commercial climate package", href: "/estimate-quotes#general" },
        },
      ]}
      afterWhyUs={
        <section id="system-picker" className="bg-cream py-14 md:py-16">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ CHOOSING THE RIGHT SYSTEM ★
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Which Heating or Cooling System Is Right for You?
            </h2>
            <div
              className="mt-8 overflow-x-auto border-2 border-navy-deep/15 bg-white"
              tabIndex={0}
              role="group"
              aria-label="Comparison of outdoor heating and cooling equipment — scrollable table"
            >
              <table className="w-full min-w-[520px] text-left text-sm">
                <caption className="sr-only">
                  Recommended starting point for each type of outdoor space
                </caption>
                <thead className="bg-navy-deep text-cream">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-display uppercase tracking-wider">
                      Type of space
                    </th>
                    <th scope="col" className="px-4 py-3 font-display uppercase tracking-wider">
                      Recommended starting point
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SYSTEM_PICKER.map((row) => (
                    <tr key={row.space} className="border-t border-navy-deep/10">
                      <th scope="row" className="px-4 py-3 font-semibold text-navy-deep">
                        {row.space}
                      </th>
                      <td className="px-4 py-3 text-navy-deep/80">{row.start}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-base leading-relaxed text-navy-deep/80">
              Prefer evaporative cooling for a fully open patio?{" "}
              <Link to="/outdoor-cooling-misting" className="font-semibold text-red underline">
                See outdoor misting cooling
              </Link>
              .
            </p>
          </div>
        </section>
      }
      applicationsEyebrow="APPLICATIONS"
      applicationsHeading="Where We Install Outdoor Heating & Cooling"
      applications={[
        "Hotel pool decks & resort courtyards",
        "Spa & wellness outdoor lounges",
        "Glamping tent resorts & safari venues",
        "Restaurant, bar & brewery patios",
        "Distillery & tasting room terraces",
        "Wedding venues & event lawns",
        "Outdoor retail showrooms & hospitality entrances",
        "Office courtyards & co-working lounges",
        "Enclosed & semi-enclosed porches",
        "Covered patios & outdoor kitchens",
        "Screened porches & three-season rooms",
        "Workshops, garages & shops",
        "Pool houses, cabanas & guest spaces",
        "Pergolas & pavilions",
        "Showrooms & small commercial spaces",
        "Event & hospitality spaces",
      ]}
      letsTalkImageSrc={hero}
      letsTalkImageAlt="Covered Texas patio at dusk with ceiling-mounted infrared heaters and refrigerated outdoor cooling keeping the space comfortable"
      faqs={FAQS}
    />
  );
}
